import React, { memo, useCallback, Fragment, useState, useEffect } from 'react';
import Dropzone from 'react-dropzone';
import { ScenarioDrillWidgetForm, ScenarioDrillBlockVariation, ScenarioForm } from './ScenarioDrillBlock';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { UploadAPI } from '@business/api/upload';
import { tap } from 'rxjs/operators';
import { Label, Row, Col } from 'reactstrap';
import { FormSelect, FormColorPicker, FormText, FormInput } from '@core/components/form';
import { CommonService } from '@business/services';
import { openImageFinderModal } from '@modules/ImageFinder';
import { FormRadioGroup, FormRadio } from '@core/components/form/FormRadio';
import { SortableList } from '@modules/common/Sortable';
import { ScenarioDrillItem } from './ScenarioDrillBlockItem';
import { SortableElement, SortableHandle, SortEnd } from 'react-sortable-hoc';
import { R } from '@core/utils/r';
import { FormHTML } from '@core/components/form/FormHTML';

export interface ScenarioDrillBlockSettingsProps {
  form: ScenarioDrillWidgetForm;
  variation: ScenarioDrillBlockVariation;
}

export const ScenarioDrillBlockSettings = memo((props: ScenarioDrillBlockSettingsProps) => {
  const { form, variation } = props;
  const scenarios = useObservable(form.controls.scenarios.items$, []);
  const scenarioValues = useObservable(form.controls.scenarios.value$, []);

  useEffect(() => {
    if (scenarios.length === 0) {
      form.controls.scenarios.push();
    }
  }, [form, scenarios]);

  const onSortEnd = useCallback(
    (sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;
      const newScenarios = R.move(scenarioValues, oldIndex, newIndex);
      form.patchValue({
        scenarios: newScenarios,
      });
    },
    [form, scenarioValues],
  );

  return (
    <div>
      <Fragment>
        {/* <FormHTML autoFocus minHeight={300} control={form.controls.instructions} /> */}
        <Row className="ml-0">
          <FormRadioGroup control={form.controls.order}>
            <FormRadio label={'Random'} value={'random'} />
            <FormRadio style={{ marginLeft: 10 }} label={'Sequence'} value={'sequence'} />
          </FormRadioGroup>
        </Row>
        {!!scenarios.length && <Label>Scenarios</Label>}
        <SortableList lockAxis="y" distance={2} onSortEnd={sort => onSortEnd(sort)} useDragHandle={true} helperClass="z5000">
          {scenarios.map((ctrl, index) => {
            return <SortableScenarioItem key={index} index={index} ctrl={ctrl} form={form} i={index} />;
          })}
        </SortableList>
      </Fragment>
    </div>
  );
});

interface ScenarioItemProps {
  i: number;
  ctrl: ScenarioForm;
  form: ScenarioDrillWidgetForm;
}

const ScenarioItem = memo((props: ScenarioItemProps) => {
  const { ctrl, form, i } = props;

  const DragHandle = SortableHandle(() => <i className="fas fa-grip-vertical font-size-16 mr-3 mb-3" style={{ cursor: 'move' }}></i>);

  return (
    <div>
      <Row key={`question-${i} mb-2 mt-2`}>
        <Col md={10} className="input-width">
          <DragHandle />
          <FormInput control={ctrl.controls.scenario} type="textarea" />
        </Col>
        <Col md={2}>
          {!i && (
            <a className="btn btn-outline-primary btn-sm mr-2" onClick={() => form.controls.scenarios.push()}>
              Add Scenario
            </a>
          )}
          {!!i && (
            <a className="btn btn-outline-danger btn-sm mr-2" onClick={() => form.controls.scenarios.removeAt(i)}>
              <i className="bx bx-x"></i>
            </a>
          )}
        </Col>
      </Row>
      <ScenarioDrillItem control={ctrl} />
      <hr />
    </div>
  );
});

export const SortableScenarioItem = SortableElement(ScenarioItem);
